import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Close from "@mui/icons-material/Close";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import Search from "@mui/icons-material/Search";
import UnpublishedOutlined from "@mui/icons-material/UnpublishedOutlined";
import { Box, ControlLabelCheckBox, CustomPagination, DateTimePicker, FormField, Modal, TitleValue, Typography } from "@maysoft/common-component-react";

import Helpers from "commons/helpers";
import Strings from "constants/strings";
import Constants from "constants/index";
import ReportService from "services/medkit/report.service";

import { DataNotFound } from "components";
import { BooleanInt, Mode, RepeatIssue } from "constants/enum";
import { ICodename, IRecordReport } from "commons/interfaces";
import { setDataAlert, showLoading } from "store/slice/common.slice";



const reportService = new ReportService();

const ModalRepeatIssueReport = ({
    data,
    visible,
    dataOptionsReportType,
    dataOptionsReportStatus,
    onClose,
    onSubmit,
}: {
    data?: IRecordReport;
    dataOptionsReportType: ICodename[];
    dataOptionsReportStatus: ICodename[];
    visible: boolean;
    onClose: () => void;
    onSubmit: () => void;
}) => {

    const dispatch = useDispatch();

    const [referenceId, setReferenceId] = useState<any>(undefined);
    const [repeatIssue, setRepeatIssue] = useState<number>(RepeatIssue.None);

    const [loadingData, setLoadingData] = useState<boolean>(false);
    const [dataReports, setDataReports] = useState<IRecordReport[]>([]);
    const [dataFilter, setDataFilter] = useState<{
        searchText?: string;
        pageSize?: number;
        pageNumber?: number;
        totalCount?: number;
    }>({});

    useEffect(() => {
        if (visible) {
            setRepeatIssue(data?.repeatIssue);
            setReferenceId(data?.referenceId);

            if (data?.repeatIssue === RepeatIssue.RepeatedUnconfirmed) {
                getPagedReferenceReports({ pageNumber: 1 });
            };
        }
    }, [visible, data?.repeatIssue, data?.referenceId]);

    const getPagedReferenceReports = async (req?: {
        searchText?: string;
        pageSize?: number;
        pageNumber?: number;
    }) => {
        try {
            setLoadingData(true);

            const result = await reportService.getPaged({
                pageSize: req.pageSize,
                pageNumber: req.pageNumber,
                searchText: req.searchText,
                repeatIssues: [RepeatIssue.None],
            });

            setDataReports([...result?.items || []]);

            setDataFilter({
                searchText: req?.searchText,
                pageSize: result?.pageSize,
                pageNumber: result?.currentPage,
                totalCount: result?.totalCount || 0
            });

        } catch (error) {
            const err = Helpers.handleExceptionError(error);
            !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
        } finally {
            setLoadingData(false);
        }
    };

    const handleClose = () => {
        setReferenceId(undefined);
        setRepeatIssue(BooleanInt.False);
        onClose();
    };

    const handleSubmit = async () => {
        try {
            dispatch(showLoading(true));

            await reportService.updateRepeatIssue({
                id: data?.id,
                referenceId: referenceId,
            });

            dispatch(setDataAlert({ message: Strings.REPORT.UPDATE_REPORT_SUCCESS, type: "success" }));

            onSubmit();
        } catch (error) {
            const err = Helpers.handleExceptionError(error);
            !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
        } finally {
            dispatch(showLoading(false));
        }
    };

    return (
        <Modal
            fullWidth
            hasActionButton
            maxWidth={"md"}
            visible={visible}
            onClose={() => { handleClose(); }}
            onAction={() => { handleSubmit(); }}
            title={"Thông tin báo cáo"}
            closeButton={Strings.COMMON.CANCEL}
            buttonAction={Strings.COMMON.CONFIRM}
        >
            <Box padding={"0px"} height={"70vh"}>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                        <TitleValue
                            title={Strings.REPORT.REPORT_CODE}
                            value={data?.reportNo || ""}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TitleValue
                            title={Strings.REPORT.REPORT_STATUS}
                            value={[...dataOptionsReportStatus || []].find(e => e.code === `${data?.reportStatus}`)?.name || ""}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TitleValue
                            title={Strings.REPORT.TYPE_REPORT}
                            value={[...dataOptionsReportType || []].find(e => e.code === `${data?.reportType}`)?.name || ""}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <DateTimePicker
                            mode={Mode.View}
                            value={Helpers.toUnix(data?.createTime)}
                            label={Strings.REPORT.CREATE_TIME_REPORT}
                            onChangeValue={(value) => { }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormField
                            multiline
                            isFullRow
                            mode={Mode.View}
                            variant={"outlined"}
                            label={Strings.REPORT.DESCRIPTION}
                            defaultValue={data?.shortDescription}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <ControlLabelCheckBox
                            label={Strings.REPORT.REPEAT_ISSUE}
                            value={repeatIssue === RepeatIssue.RepeatedUnconfirmed}
                            onChangeValue={() => {
                                if (repeatIssue === RepeatIssue.RepeatedUnconfirmed) {
                                    setReferenceId(undefined);
                                    setRepeatIssue(RepeatIssue.None);
                                } else {
                                    setReferenceId(undefined);
                                    setRepeatIssue(RepeatIssue.RepeatedUnconfirmed);
                                    getPagedReferenceReports({ pageNumber: 1 });
                                }
                            }}
                        />
                    </Grid>

                    {(repeatIssue === RepeatIssue.RepeatedUnconfirmed) &&
                        <Grid item xs={12}>
                            <Box>
                                <Typography variant="button" fontWeight="bold">
                                    {Strings.REPORT.REFERENCE_INCIDENT_LIST}
                                </Typography>
                                <Box padding={"8px 0px"}>
                                    <FormField
                                        fullWidth
                                        size={"small"}
                                        maxLength={255}
                                        variant={"outlined"}
                                        defaultValue={dataFilter?.searchText || ""}
                                        placeholder={Strings.REPORT.PLACEHOLDER_SEARCH_TEXT}
                                        onBlur={(e: any) => {
                                            getPagedReferenceReports({ pageNumber: 1, searchText: e.target.value });
                                        }}
                                        onKeyPress={(e: any) => {
                                            if (e.key === "Enter") {
                                                getPagedReferenceReports({ pageNumber: 1, searchText: e.target.value });
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <IconButton
                                                    color="info"
                                                    onClick={() => { getPagedReferenceReports({ ...dataFilter }); }}
                                                >
                                                    <Search />
                                                </IconButton>
                                            ),
                                            endAdornment: !Helpers.isNullOrEmpty(dataFilter?.searchText) && (
                                                <IconButton
                                                    color="secondary"
                                                    onClick={() => { getPagedReferenceReports({ pageNumber: 1, searchText: undefined }); }}
                                                >
                                                    <Close />
                                                </IconButton>
                                            ),
                                        }}
                                    />
                                </Box>
                                {loadingData &&
                                    <Box
                                        sx={{
                                            height: "200px",
                                            display: "flex",
                                            borderRadius: 2,
                                            alignItems: "center",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            backgroundColor: "#f3f3f3",
                                        }}
                                    >
                                        <CircularProgress />
                                    </Box>
                                }
                                {!loadingData && (dataReports.length > 0) &&
                                    <Box sx={{
                                        display: "grid",
                                        overflow: "auto",
                                        borderRadius: "8px",
                                        border: "1px solid #cdcdcd",
                                    }}>
                                        <Box sx={{
                                            gap: 1,
                                            display: "grid",
                                            padding: "8px 12px",
                                            backgroundColor: "#e5e7eb",
                                            borderTopLeftRadius: "8px",
                                            borderTopRightRadius: "8px",
                                            gridTemplateColumns: "30px 150px 100px 100px 150px 1fr",
                                        }}>
                                            <Typography>
                                                {""}
                                            </Typography>
                                            <Typography variant="caption" fontWeight="bold" color="secondary">
                                                {Strings.REPORT.REPORT_CODE}
                                            </Typography>
                                            <Typography variant="caption" fontWeight="bold" color="secondary">
                                                {Strings.COMMON.STATUS}
                                            </Typography>
                                            <Typography variant="caption" fontWeight="bold" color="secondary">
                                                {Strings.REPORT.TYPE_REPORT}
                                            </Typography>
                                            <Typography variant="caption" fontWeight="bold" color="secondary">
                                                {Strings.REPORT.CREATE_TIME_REPORT}
                                            </Typography>
                                            <Typography variant="caption" fontWeight="bold" color="secondary">
                                                {Strings.REPORT.DESCRIPTION}
                                            </Typography>
                                        </Box>
                                        {dataReports.map((item) => (
                                            <Box
                                                key={item.id}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (item.id === data?.id) {

                                                    } else {
                                                        setReferenceId(item.id);
                                                    }
                                                }}
                                                sx={{
                                                    gap: 1,
                                                    display: "grid",
                                                    padding: "8px 12px",
                                                    gridTemplateColumns: "30px 150px 100px 100px 150px 1fr",
                                                    backgroundColor: (referenceId === item.id) ? "#f0f8ff" : "#ffffff",
                                                    "&:hover": {
                                                        cursor: "pointer",
                                                    },
                                                    ...(item.id === data?.id) && {
                                                        backgroundColor: "#f3f3f3",
                                                        "&:hover": {
                                                            cursor: "auto",
                                                        },
                                                        ".MuiTypography-root": {
                                                            textDecoration: "line-through"
                                                        },
                                                    }
                                                }}
                                            >
                                                <>
                                                    {(item.id === data?.id)
                                                        ? <UnpublishedOutlined />
                                                        : ((referenceId === item.id) ? <CheckCircle /> : <RadioButtonUnchecked />)
                                                    }
                                                </>
                                                <Typography variant="caption">
                                                    {item.reportNo}
                                                </Typography>
                                                <Typography variant="caption">
                                                    {[...dataOptionsReportStatus || []].find(e => e.code === item.reportStatus)?.name || ""}
                                                </Typography>
                                                <Typography variant="caption">
                                                    {[...dataOptionsReportType || []].find(e => e.code === item.reportType)?.name || ""}
                                                </Typography>
                                                <Typography variant="caption">
                                                    {Helpers.dateToString(Number(item.createTime), Constants.DateFormat.DDMMYYYY_HHMM)}
                                                </Typography>
                                                <Typography variant="caption">
                                                    {item.shortDescription}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                }
                                {/* no data */}
                                {!loadingData && (dataReports.length === 0) &&
                                    <Box textAlign="center">
                                        <DataNotFound height={"200px"} />
                                    </Box>
                                }
                                {!loadingData && (dataReports.length > 0) &&
                                    <Box>
                                        <CustomPagination
                                            isList
                                            page={dataFilter?.pageNumber}
                                            total={dataFilter?.totalCount}
                                            pageSize={dataFilter?.pageSize}
                                            rowsPerPageOptions={Constants.ROW_PER_PAGE_OPTIONS}
                                            onChangePage={(page) => {
                                                getPagedReferenceReports({ ...dataFilter, pageNumber: page });
                                            }}
                                        />
                                    </Box>
                                }
                            </Box>
                        </Grid>
                    }
                </Grid>
            </Box>
        </Modal>
    );
}

export default ModalRepeatIssueReport;